import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import '../../site.css';
import { adminMe } from '../../api/admin';
import { NARROW, useMediaQuery } from '../../shared/media';
import type { SiteLook } from '../../shared/siteContent';
import {
  SiteContentContext, isSitePreview, usePreviewContent, usePreviewHeight, useSiteContent,
} from './useSiteContent';

/**
 * The public site's chrome: sticky nav, footer, and one answer to "who is
 * looking?". Every marketing and community page renders inside this.
 *
 * The viewer is resolved once per mount by asking /admin/me and treating 401
 * as the normal case — most visitors are strangers. Community pages read it
 * from context to decide between composers and "sign in to post".
 *
 * R13: the handoff's bar (`A Site Nav`) — 64px, blurred white, edge to edge;
 * Features · Pricing · FAQ · Community; Sign in and the ink "Start free".
 * Every call to action still goes through /pricing
 * (`signupStartsAtPricing.test`), so "Start free" lands on the plans, where
 * the Free card is the first one.
 *
 * The Jotform comparison, its "Compare" link and the footer's trademark
 * line were removed on Ben's decision (2026-09-27): the product stands on
 * its own merits, so the site names no competitor.
 *
 * Site content (078): the pages draw the published document, which this
 * layout reads once and hands down (`useSite`), and the Look is three
 * attributes on the root that re-point the site's own tokens (site.css,
 * "The look"). Each attribute is absent for the launch look — indigo, Geist,
 * rounded — so that site is the same DOM, and the same pixels, it always
 * was. Framed by the console at ?preview=draft, it draws the posted draft
 * instead, as a stranger would see it.
 */

/** The Look as attributes on .site — only where it differs from the launch look. */
function lookAttrs(look: SiteLook) {
  return {
    'data-accent': look.accent !== 'indigo' ? look.accent : undefined,
    'data-headings': look.font !== 'sans' ? look.font : undefined,
    'data-corners': look.radius !== 'round' ? look.radius : undefined,
  };
}

export interface SiteViewer {
  name: string;
  isStaff: boolean;
}

interface SiteAuth {
  /** null = anonymous; undefined = still asking */
  viewer: SiteViewer | null | undefined;
}

const SiteAuthContext = createContext<SiteAuth>({ viewer: undefined });
export const useSiteViewer = () => useContext(SiteAuthContext).viewer;

/** The four destinations, in the design's order — the bar and the phone sheet read the one list. */
const LINKS: { to: string; label: string }[] = [
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
  { to: '/community', label: 'Community' },
];

/** The FM mark is decoration: the link's name is "Form-Magic". */
export function SiteLogo({ className = 'site-logo' }: { className?: string }) {
  return <Link to="/" className={className}><span className="mark" aria-hidden="true">FM</span>Form-Magic</Link>;
}

export default function SiteLayout() {
  // The console's preview: read once — a frame does not stop being one.
  const [preview] = useState(isSitePreview);
  const published = useSiteContent();
  const drafted = usePreviewContent(preview);
  const content = preview ? drafted : published;
  const root = useRef<HTMLDivElement>(null);
  usePreviewHeight(preview, root);

  const [viewer, setViewer] = useState<SiteViewer | null | undefined>(undefined);
  // Under 900px the links live in a sheet behind a menu button (F1). The
  // button is not rendered at all on a desktop. The sheet closes on route
  // change, Escape and the scrim.
  const narrow = useMediaQuery(NARROW);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    // The preview is what a stranger sees, whoever is looking at the console.
    if (preview) { setViewer(null); return undefined; }
    let alive = true;
    adminMe()
      .then((me) => {
        if (!alive) return;
        setViewer({ name: me.admin?.name || me.admin?.email || 'You', isStaff: me.isPlatformAdmin });
      })
      .catch(() => { if (alive) setViewer(null); });
    return () => { alive = false; };
  }, [preview]);

  // The first paint waits for the words (see useSiteContent).
  if (!content) return <div className="site" aria-busy="true" />;

  return (
    <SiteAuthContext.Provider value={{ viewer }}>
      <div className="site" ref={root} {...lookAttrs(content.doc.look)} data-preview={preview ? '' : undefined}>
        <header className="site-nav">
          <div className="inner">
            <SiteLogo />
            <nav className="links" aria-label="Site">
              {LINKS.map((l) => <NavLink key={l.to} to={l.to}>{l.label}</NavLink>)}
            </nav>
            <div className="auth">
              {viewer ? (
                <Link className="fs-btn ink" to="/admin">Open dashboard</Link>
              ) : (
                <>
                  <Link className="signin" to="/login">Sign in</Link>
                  <Link className="fs-btn ink" to="/pricing">Start free</Link>
                </>
              )}
              {narrow && (
                <button
                  type="button" className="site-menu-btn" aria-label="Menu" aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((o) => !o)}
                >
                  {menuOpen ? '✕' : '☰'}
                </button>
              )}
            </div>
          </div>
          {narrow && menuOpen && (
            <>
              <div className="site-menu-scrim" onClick={() => setMenuOpen(false)} />
              <nav className="site-menu" aria-label="Site menu">
                {LINKS.map((l) => <NavLink key={l.to} to={l.to}>{l.label}</NavLink>)}
                <div className="rule" />
                {viewer ? (
                  <Link className="fs-btn ink" to="/admin">Open dashboard</Link>
                ) : (
                  <>
                    <NavLink to="/login">Sign in</NavLink>
                    <Link className="fs-btn ink" to="/pricing">Start free</Link>
                  </>
                )}
              </nav>
            </>
          )}
        </header>

        <main><SiteContentContext.Provider value={content}><Outlet /></SiteContentContext.Provider></main>

        <footer className="site-footer">
          <div className="inner">
            <div>
              <SiteLogo />
              <p className="blurb">
                Design forms, collect and review submissions, connect delivery
                integrations, and bring your own single sign-on.
              </p>
            </div>
            <div>
              <h4>Product</h4>
              <Link to="/features">Features</Link>
              <Link to="/pricing">Pricing</Link>
              <Link to="/faq">FAQ</Link>
            </div>
            <div>
              <h4>Community</h4>
              <Link to="/community">Support forum</Link>
              <Link to="/community/feature-requests">Feature requests</Link>
              <Link to="/community/releases">Release history</Link>
            </div>
            <div>
              <h4>Account</h4>
              <Link to="/pricing">Create an account</Link>
              <Link to="/login">Sign in</Link>
            </div>
          </div>
          <div className="legal">© {new Date().getFullYear()} Form-Magic.</div>
        </footer>
      </div>
    </SiteAuthContext.Provider>
  );
}
